import { useCallback, useEffect, useRef, useState } from 'react';
import { enviarFormulario } from '../api';
/* O mesmo limite de todas as outras telas que mandam foto, e do mesmo
   lugar: dois números iguais em dois arquivos um dia divergem. */
import { LIMITE_POR_ENVIO } from '../Fotos';
import { dataHora, rotulo } from '../formato';
import { useSessao } from '../sessao';
import {
  Area, Aviso, Campo, Dialogo, ErroDaApi, Escolha, FotoProtegida,
  LinkProtegido, Rn, Selo, ZonaDeArquivo,
} from '../ui';
import { SeloPdf } from './Equipamento';
import {
  abrirManutencao, autorizarUso, cancelarManutencao, definirCapa,
  desconsiderarFoto, encerrarManutencao, listarAutorizacoes, listarFotos,
  listarManutencoes, revogarAutorizacao,
  RESULTADOS_MANUTENCAO, TIPOS_FOTO, TIPOS_MANUTENCAO,
  type AutorizacaoUso, type FotoEquipamento, type Manutencao,
} from '../api/equipamento';

/**
 * Fotografia e manutenção na ficha do equipamento — a tela da `0053`.
 *
 * ── A ordem, e por que ela é esta ───────────────────────────────────
 *
 * A manutenção vem ANTES das fotografias, embora a fotografia seja o
 * assunto mais leve. É a manutenção que muda o que a pessoa pode fazer
 * com o equipamento hoje: se há uma aberta, o equipamento está fora de
 * serviço e a resposta à pergunta do topo da ficha — «posso usar isto?»
 * — está aqui embaixo. Enterrar isso depois de uma grade de imagens
 * seria esconder a informação operacional atrás da ilustrativa.
 *
 * ── O que a tela não decide ─────────────────────────────────────────
 *
 * Nada sobre status. Quem tira o equipamento de serviço ao abrir, e quem
 * decide para onde ele volta ao encerrar, é o banco (`RN-179`,
 * `RN-180`). A tela pergunta, mostra e avisa; não conclui.
 */

// ─────────────────────────────────────────────────────────────────────
// Manutenção
// ─────────────────────────────────────────────────────────────────────

/**
 * Anexar o laudo do fornecedor a uma manutenção que entrou sem ele.
 *
 * Mesmo desenho do `AnexarPdf` do certificado, e pelo mesmo motivo: o
 * seletor abre direto no clique. Escolher um arquivo é ação de um passo,
 * e um diálogo no meio é um passo a mais entre a pessoa e a pasta que
 * ela já tem aberta.
 */
function AnexarDocumento({ m, aoAnexar }: {
  m: Manutencao; aoAnexar(): void;
}) {
  const { pode } = useSessao();
  const entrada = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  if (!pode('arquivo.enviar')) return <SeloPdf tem={false} />;

  async function enviar(arquivo: File) {
    setErro(null); setEnviando(true);
    try {
      const form = new FormData();
      form.append('documento', arquivo, arquivo.name);
      await enviarFormulario(
        `/equipamentos/manutencoes/${m.id}/documento`, form);
      aoAnexar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao anexar.');
    } finally { setEnviando(false); }
  }

  return (
    <>
      <input
        ref={entrada}
        type="file"
        accept="application/pdf,.pdf"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) void enviar(f);
        }}
      />
      <button
        type="button"
        className="como-link"
        disabled={enviando}
        title="Anexar o laudo, a ordem de serviço ou o relatório"
        onClick={() => entrada.current?.click()}
      >
        {enviando ? 'enviando…' : <SeloPdf tem={false} />}
      </button>
      {erro && <div className="erro">{erro}</div>}
    </>
  );
}

/** Formata o `interval` do Postgres, que chega como objeto. */
function parada(p: Manutencao['parada']): string {
  if (!p) return '—';
  const d = p.days ?? 0;
  const h = p.hours ?? 0;
  const min = p.minutes ?? 0;
  if (d > 0) return `${d} dia(s) ${h} h`;
  if (h > 0) return `${h} h ${min} min`;
  return `${min} min`;
}

export function SecaoManutencao({
  equipamento, codigo, aoMudar,
}: { equipamento: string; codigo: string; aoMudar(): void }) {
  const { pode } = useSessao();
  const [lista, setLista] = useState<Manutencao[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [form, setForm] = useState<
    { que: 'abrir' } | { que: 'encerrar' | 'cancelar'; m: Manutencao } | null>(null);

  const carregar = useCallback(() => {
    listarManutencoes(equipamento).then(setLista).catch(setErro);
  }, [equipamento]);
  useEffect(carregar, [carregar]);

  const fechar = (mudou: boolean) => {
    setForm(null);
    if (mudou) { carregar(); aoMudar(); }
  };

  const aberta = lista?.find((m) => m.aberta) ?? null;

  return (
    <div className="cartao">
      <div className="cartao-cabeca">
        <h2>Manutenção</h2>
        {pode('equipamento.editar') && !aberta && (
          <button
            type="button" className="botao discreto"
            onClick={() => setForm({ que: 'abrir' })}
          >
            + Abrir manutenção
          </button>
        )}
      </div>

      <p className="sub">
        Abrir uma manutenção <b>tira o equipamento de serviço</b>{' '}
        <Rn codigo="RN-179"
          diz="Enquanto há manutenção aberta, o equipamento não entra em ensaio." />.
        No encerramento, o sistema pergunta se o serviço pode ter alterado o
        que o equipamento mede — e, se puder, o certificado anterior deixa de
        amparar o uso{' '}
        <Rn codigo="RN-180"
          diz="Manutenção que interfere na medição exige calibração externa ou interna posterior." />.
      </p>

      <ErroDaApi erro={erro} />

      {aberta && (
        <Aviso tipo="atencao" titulo="Manutenção em andamento">
          {rotulo(aberta.tipo)} desde {dataHora(aberta.abertura)} ·{' '}
          parada de {parada(aberta.parada)}.
          <div style={{ marginTop: '.4rem' }}>{aberta.motivo}</div>
          {pode('equipamento.editar') && (
            <div className="linha" style={{ marginTop: '.6rem' }}>
              <button
                type="button" className="botao"
                onClick={() => setForm({ que: 'encerrar', m: aberta })}
              >
                Encerrar
              </button>
              {pode('equipamento.cancelar_manutencao') && (
                <button
                  type="button" className="botao discreto"
                  onClick={() => setForm({ que: 'cancelar', m: aberta })}
                >
                  Cancelar — foi engano
                </button>
              )}
            </div>
          )}
        </Aviso>
      )}

      {lista === null && <p className="nada">Carregando…</p>}

      {lista !== null && lista.length === 0 && (
        <p className="nada">Nenhuma manutenção registrada.</p>
      )}

      {lista !== null && lista.length > 0 && (
        <div className="rola">
          <table className="dados">
            <thead>
              <tr>
                <th>Quando</th><th>Tipo</th><th>Motivo e serviço</th>
                <th>Parada</th><th>Papel</th><th>Situação</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((m) => (
                <tr key={m.id} style={m.cancelada ? { opacity: .55 } : undefined}>
                  <td>
                    {dataHora(m.abertura)}
                    <div className="sub">{m.aberta_por_nome ?? '—'}</div>
                  </td>
                  <td>{rotulo(m.tipo)}</td>
                  <td>
                    {m.motivo}
                    {m.servico_executado && (
                      <div className="sub">→ {m.servico_executado}</div>
                    )}
                    {m.pecas_substituidas && (
                      <div className="sub">peças: {m.pecas_substituidas}</div>
                    )}
                    {m.executante && (
                      <div className="sub">
                        {m.executante_externo ? 'externo: ' : ''}{m.executante}
                        {m.numero_documento && ` · doc ${m.numero_documento}`}
                      </div>
                    )}
                    {m.cancelada && (
                      <div className="sub">
                        cancelada por {m.cancelada_por_nome ?? '—'}:{' '}
                        {m.motivo_cancelamento}
                      </div>
                    )}
                  </td>
                  <td className="num">{parada(m.parada)}</td>
                  {/*
                    * O laudo do fornecedor, em PDF (`0113`).
                    *
                    * A `RN-180` decide se a manutenção obriga a recalibrar,
                    * e quem decide isso lê o laudo. Sem ele no sistema, a
                    * decisão fica registrada e a evidência fica na gaveta.
                    */}
                  <td>
                    {m.arquivo_id
                      ? (
                        <LinkProtegido
                          caminho={`/arquivos/${m.arquivo_id}/conteudo`}
                          nomeQueda={`manutencao-${m.id.slice(0, 8)}.pdf`}
                        >
                          <SeloPdf tem />
                        </LinkProtegido>
                      )
                      : <AnexarDocumento m={m} aoAnexar={carregar} />}
                  </td>
                  <td>
                    {m.cancelada && <Selo tom="neutro">Cancelada</Selo>}
                    {!m.cancelada && m.aberta && <Selo tom="atencao">Aberta</Selo>}
                    {!m.cancelada && !m.aberta && (
                      m.exige_recalibracao
                        ? <Selo tom="critico">Exigiu recalibração</Selo>
                        : <Selo tom="ok">{rotulo(m.resultado ?? 'concluida')}</Selo>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {form?.que === 'abrir' && (
        <FormAbrir equipamento={equipamento} codigo={codigo} aoFechar={fechar} />
      )}
      {form?.que === 'encerrar' && (
        <FormEncerrar m={form.m} aoFechar={fechar} />
      )}
      {form?.que === 'cancelar' && (
        <FormCancelar m={form.m} aoFechar={fechar} />
      )}
    </div>
  );
}

function FormAbrir({
  equipamento, codigo, aoFechar,
}: { equipamento: string; codigo: string; aoFechar(mudou: boolean): void }) {
  const [tipo, setTipo] = useState<string | null>('corretiva');
  const [motivo, setMotivo] = useState('');
  const [externo, setExterno] = useState(false);
  const [executante, setExecutante] = useState('');
  const [documento, setDocumento] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const enviar = async () => {
    setEnviando(true); setErro(null);
    try {
      await abrirManutencao(equipamento, {
        tipo, motivo, executanteExterno: externo,
        executante: executante || undefined,
        numeroDocumento: documento || undefined,
      });
      aoFechar(true);
    } catch (e) { setErro(e); setEnviando(false); }
  };

  return (
    <Dialogo aberto titulo={`Abrir manutenção — ${codigo}`} aoFechar={() => aoFechar(false)}>
      <Aviso tipo="info" titulo="O equipamento sai de serviço agora">
        Enquanto a manutenção estiver aberta, ele não aparece como apto e não
        entra em ensaio. É o que a NBR ISO/IEC 17025, 6.4.4, pede.
      </Aviso>

      <ErroDaApi erro={erro} />

      <Escolha
        rotulo="Tipo"
        valor={tipo}
        aoEscolher={setTipo}
        limpavel={false}
        opcoes={TIPOS_MANUTENCAO.map(([v, t]) => ({ valor: v, texto: t }))}
        dica={TIPOS_MANUTENCAO.find(([v]) => v === tipo)?.[2]}
      />

      <Area
        rotulo="O que levou à manutenção"
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        rows={3}
        dica="Quem ler isto daqui a dois anos precisa entender o que aconteceu."
      />

      <Escolha
        rotulo="Quem executa"
        valor={externo ? 'externo' : 'interno'}
        aoEscolher={(v) => setExterno(v === 'externo')}
        limpavel={false}
        opcoes={[
          { valor: 'interno', texto: 'Equipe da casa' },
          { valor: 'externo', texto: 'Empresa externa' },
        ]}
      />

      <Campo
        rotulo={externo ? 'Empresa' : 'Responsável (opcional)'}
        value={executante}
        onChange={(e) => setExecutante(e.target.value)}
      />

      <Campo
        rotulo="Nº do chamado, OS do fornecedor ou nota (opcional)"
        value={documento}
        onChange={(e) => setDocumento(e.target.value)}
      />

      <div className="linha" style={{ marginTop: '.9rem' }}>
        <button type="button" className="botao" onClick={enviar} disabled={enviando}>
          {enviando ? 'Abrindo…' : 'Abrir e tirar de serviço'}
        </button>
        <button
          type="button" className="botao secundario"
          onClick={() => aoFechar(false)}
        >
          Cancelar
        </button>
      </div>
    </Dialogo>
  );
}

/**
 * O encerramento.
 *
 * A pergunta da `RN-180` não tem valor pré-escolhido de propósito: um
 * «não» já marcado seria a resposta errada na metade dos casos, e é a
 * perigosa das duas — deixa o equipamento voltar ao serviço amparado por
 * um certificado anterior ao reparo.
 */
function FormEncerrar({
  m, aoFechar,
}: { m: Manutencao; aoFechar(mudou: boolean): void }) {
  const [servico, setServico] = useState('');
  const [pecas, setPecas] = useState('');
  const [resultado, setResultado] = useState<string | null>('concluida');
  const [recal, setRecal] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const enviar = async () => {
    setEnviando(true); setErro(null);
    try {
      await encerrarManutencao(m.id, {
        servicoExecutado: servico,
        pecasSubstituidas: pecas || undefined,
        resultado,
        exigeRecalibracao: recal === 'sim',
      });
      aoFechar(true);
    } catch (e) { setErro(e); setEnviando(false); }
  };

  return (
    <Dialogo aberto titulo="Encerrar manutenção" aoFechar={() => aoFechar(false)}>
      <ErroDaApi erro={erro} />

      <Area
        rotulo="Serviço executado"
        value={servico}
        onChange={(e) => setServico(e.target.value)}
        rows={3}
        dica="É este texto que explica, depois, por que o certificado anterior vale ou não vale."
      />

      <Campo
        rotulo="Peças substituídas (opcional)"
        value={pecas}
        onChange={(e) => setPecas(e.target.value)}
      />

      <Escolha
        rotulo="Resultado"
        valor={resultado}
        aoEscolher={setResultado}
        limpavel={false}
        opcoes={RESULTADOS_MANUTENCAO.map(([v, t]) => ({ valor: v, texto: t }))}
      />

      <Escolha
        rotulo="O serviço pode ter alterado o que o equipamento mede?"
        valor={recal}
        aoEscolher={setRecal}
        limpavel={false}
        opcoes={[
          { valor: 'nao', texto: 'Não' },
          { valor: 'sim', texto: 'Sim' },
        ]}
        dica="Troca de cabo, pintura, limpeza externa: não. Ajuste de zero ou de
              span, troca de sensor, abertura do conjunto de medição: sim."
      />

      {recal === 'sim' && (
        <Aviso tipo="atencao" titulo="O equipamento ficará bloqueado">
          O certificado e a calibração interna anteriores deixam de amparar o uso. Ele só
          volta a ficar apto depois de uma calibração externa ou interna
          <b> posterior a este encerramento</b> — NBR ISO/IEC 17025, 6.4.4.
        </Aviso>
      )}

      {recal === 'nao' && (
        <Aviso tipo="info" titulo="O equipamento volta ao serviço">
          Volta ao status que tinha antes de a manutenção abrir.
        </Aviso>
      )}

      <div className="linha" style={{ marginTop: '.9rem' }}>
        <button
          type="button" className="botao" onClick={enviar}
          disabled={enviando || recal === null}
        >
          {enviando ? 'Encerrando…' : 'Encerrar'}
        </button>
        <button
          type="button" className="botao secundario"
          onClick={() => aoFechar(false)}
        >
          Voltar
        </button>
      </div>
    </Dialogo>
  );
}

function FormCancelar({
  m, aoFechar,
}: { m: Manutencao; aoFechar(mudou: boolean): void }) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const enviar = async () => {
    setEnviando(true); setErro(null);
    try { await cancelarManutencao(m.id, motivo); aoFechar(true); }
    catch (e) { setErro(e); setEnviando(false); }
  };

  return (
    <Dialogo aberto titulo="Cancelar manutenção" aoFechar={() => aoFechar(false)}>
      <Aviso tipo="atencao" titulo="Cancelar não é encerrar">
        Use isto só quando a manutenção foi aberta por engano — no patrimônio
        errado, ou em duplicidade. Se o serviço aconteceu, encerre: cancelar
        apaga a parada do indicador de disponibilidade.
      </Aviso>

      <ErroDaApi erro={erro} />

      <Area
        rotulo="Por que está cancelando"
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        rows={2}
      />

      <div className="linha" style={{ marginTop: '.9rem' }}>
        <button type="button" className="botao" onClick={enviar} disabled={enviando}>
          {enviando ? 'Cancelando…' : 'Cancelar a manutenção'}
        </button>
        <button
          type="button" className="botao secundario"
          onClick={() => aoFechar(false)}
        >
          Voltar
        </button>
      </div>
    </Dialogo>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Fotografias
// ─────────────────────────────────────────────────────────────────────

/**
 * As fotografias do equipamento (briefing §14).
 *
 * `capture` no input não força a câmera no computador — ali continua
 * abrindo o seletor de arquivos. No celular, abre a câmera direto, que é
 * onde essas fotos nascem: alguém de pé na frente da balança.
 */
export function SecaoFotos({
  equipamento, codigo,
}: { equipamento: string; codigo: string }) {
  const { pode } = useSessao();
  const [lista, setLista] = useState<FotoEquipamento[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [progresso, setProgresso] = useState<number | null>(null);
  const [tipo, setTipo] = useState<string | null>('geral');
  const [saindo, setSaindo] = useState<FotoEquipamento | null>(null);
  const entrada = useRef<HTMLInputElement>(null);

  const carregar = useCallback(() => {
    listarFotos(equipamento).then(setLista).catch(setErro);
  }, [equipamento]);
  useEffect(carregar, [carregar]);

  /**
   * Em levas de dez, como o recebimento.
   *
   * O servidor aceita no máximo `LIMITE_ARQUIVOS_LOTE` arquivos por
   * requisição. Isto mandava todos de uma vez, e quem selecionasse doze
   * fotos do parque recebia «Too many files» — em inglês, e sem que
   * nenhuma subisse. Foi o teste `26.4` que achou: um defeito de FORMA,
   * o mesmo que o documento 72 registrou no recebimento.
   */
  const enviar = async (arquivos: FileList) => {
    if (!arquivos.length) return;
    setErro(null); setProgresso(0);
    const todos = Array.from(arquivos);
    const blocos: File[][] = [];
    for (let i = 0; i < todos.length; i += LIMITE_POR_ENVIO) {
      blocos.push(todos.slice(i, i + LIMITE_POR_ENVIO));
    }
    try {
      for (let b = 0; b < blocos.length; b++) {
        const fd = new FormData();
        for (const a of blocos[b]) fd.append('fotos', a);
        fd.append('itens', JSON.stringify(
          blocos[b].map(() => ({ tipo: tipo ?? 'geral' }))));
        await enviarFormulario(
          `/equipamentos/${equipamento}/fotos`, fd,
          (fr) => setProgresso((b + fr) / blocos.length));
      }
      carregar();
    } catch (e) { setErro(e); }
    finally {
      setProgresso(null);
      if (entrada.current) entrada.current.value = '';
    }
  };

  return (
    <div className="cartao">
      <div className="cartao-cabeca">
        <h2>Fotografias</h2>
      </div>

      <p className="sub">
        A plaqueta patrimonial e o número de série são as duas que importam:
        são elas que amarram o certificado ao objeto que está na bancada. As
        demais ajudam a reconhecer o equipamento e a registrar avarias.
      </p>

      <ErroDaApi erro={erro} />

      {pode('arquivo.enviar') && (
        <>
          <Escolha
            rotulo="O que a próxima foto mostra"
            valor={tipo}
            aoEscolher={setTipo}
            limpavel={false}
            opcoes={TIPOS_FOTO.map(([v, t]) => ({ valor: v, texto: t }))}
          />
          {/*
            * A câmera continua num `<input capture>` próprio, escondido,
            * e a zona cuida do arquivo que já existe.
            *
            * São dois gestos diferentes e a mesma área não serve aos
            * dois: no celular, `capture` abre a câmera direto — que é o
            * certo na bancada —, e no computador ele não faz nada e
            * ainda impede arrastar. Um botão para fotografar, uma zona
            * para soltar o arquivo.
            */}
          <input
            ref={entrada}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            hidden
            aria-label={`Fotografar ${codigo}`}
            onChange={(e) => {
              if (e.target.files) void enviar(e.target.files);
              e.target.value = '';
            }}
          />
          <ZonaDeArquivo
            rotulo="Fotografia"
            aceita="image/*"
            tipos="Imagens"
            multiplo
            textoVazio="Arraste as fotos aqui"
            dica="Ou use a câmera, no botão abaixo."
            aoEscolher={(fs) => {
              const dt = new DataTransfer();
              fs.forEach((f) => dt.items.add(f));
              void enviar(dt.files);
            }}
          />
          <button type="button" className="botao discreto"
            onClick={() => entrada.current?.click()}>
            Fotografar agora
          </button>
          {progresso !== null && (
            <p className="sub">Enviando… {Math.round(progresso * 100)}%</p>
          )}
        </>
      )}

      {lista !== null && lista.length === 0 && (
        <p className="nada">Nenhuma fotografia. Comece pela plaqueta.</p>
      )}

      {lista !== null && lista.length > 0 && (
        <div className="grade-fotos">
          {lista.map((f) => (
            <figure key={f.id} className="foto">
              <FotoProtegida arquivoId={f.arquivo_id} alt={f.legenda ?? rotulo(f.tipo)} />
              <figcaption className="leg">
                <b>{rotulo(f.tipo)}</b>
                {f.principal && <> · <Selo tom="ok">Capa</Selo></>}
                {f.legenda && <div className="sub">{f.legenda}</div>}
                <div className="sub">
                  {dataHora(f.enviada_em)}
                  {f.enviada_por_nome && ` · ${f.enviada_por_nome}`}
                </div>
                {pode('equipamento.editar') && (
                  <div className="linha">
                    {!f.principal && (
                      <button
                        type="button" className="botao discreto"
                        onClick={() => definirCapa(f.id).then(carregar).catch(setErro)}
                      >
                        Usar como capa
                      </button>
                    )}
                    <button
                      type="button" className="botao discreto"
                      onClick={() => setSaindo(f)}
                    >
                      Tirar do documento
                    </button>
                  </div>
                )}
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      {saindo && (
        <FormDesconsiderar
          foto={saindo}
          aoFechar={(mudou) => { setSaindo(null); if (mudou) carregar(); }}
        />
      )}
    </div>
  );
}

function FormDesconsiderar({
  foto, aoFechar,
}: { foto: FotoEquipamento; aoFechar(mudou: boolean): void }) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const enviar = async () => {
    setEnviando(true); setErro(null);
    try { await desconsiderarFoto(foto.id, motivo); aoFechar(true); }
    catch (e) { setErro(e); setEnviando(false); }
  };

  return (
    <Dialogo aberto titulo="Tirar a fotografia do documento" aoFechar={() => aoFechar(false)}>
      <Aviso tipo="info" titulo="A fotografia não é apagada">
        Ela sai do documento e continua no banco, com o motivo e o seu nome —
        some a evidência e some o registro de que ela existiu.
      </Aviso>

      <ErroDaApi erro={erro} />

      <Area
        rotulo="Por quê"
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        rows={2}
      />

      <div className="linha" style={{ marginTop: '.9rem' }}>
        <button type="button" className="botao" onClick={enviar} disabled={enviando}>
          {enviando ? 'Gravando…' : 'Tirar do documento'}
        </button>
        <button
          type="button" className="botao secundario"
          onClick={() => aoFechar(false)}
        >
          Voltar
        </button>
      </div>
    </Dialogo>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Autorização de uso excepcional (`0054`)
// ─────────────────────────────────────────────────────────────────────

/**
 * A saída legítima da `RN-151`.
 *
 * A trava mora no banco: equipamento impedido não entra em ensaio. Uma
 * regra sem saída legítima é uma regra que alguém contorna por fora —
 * lançando o ensaio noutro equipamento, ou no papel. O briefing prevê a
 * saída (§14): autorização formal de quem tem permissão, com
 * justificativa e prazo.
 *
 * A seção só aparece quando há impedimento OU quando já houve
 * autorização. Num equipamento em dia ela seria um convite.
 */
export function SecaoAutorizacoes({
  equipamento, impedido, aoMudar,
}: { equipamento: string; impedido: boolean; aoMudar(): void }) {
  const { pode } = useSessao();
  const [lista, setLista] = useState<AutorizacaoUso[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [autorizando, setAutorizando] = useState(false);
  const [revogando, setRevogando] = useState<AutorizacaoUso | null>(null);

  const carregar = useCallback(() => {
    listarAutorizacoes(equipamento).then(setLista).catch(setErro);
  }, [equipamento]);
  useEffect(carregar, [carregar]);

  if (lista === null) return null;
  if (!impedido && lista.length === 0) return null;

  const vigente = lista.find((a) => a.vigente) ?? null;

  return (
    <div className="cartao">
      <div className="cartao-cabeca">
        <h2>Uso excepcional</h2>
        {impedido && !vigente && pode('equipamento.liberar_bloqueado') && (
          <button
            type="button" className="botao discreto"
            onClick={() => setAutorizando(true)}
          >
            Autorizar mesmo assim
          </button>
        )}
      </div>

      <p className="sub">
        Equipamento impedido não entra em ensaio — a trava é do banco, não
        da tela{' '}
        <Rn codigo="RN-151"
          diz="Ensaio não seleciona equipamento sem calibração externa ou interna válida." />.
        O briefing permite a exceção, e exige as três coisas que este
        formulário pede: <b>quem</b> autoriza tem a permissão, <b>por quê</b>{' '}
        fica escrito, e há <b>prazo</b>.
      </p>

      <ErroDaApi erro={erro} />

      {vigente && (
        <Aviso tipo="atencao" titulo="Há autorização vigente">
          Até {dataHora(vigente.valida_ate)} · {vigente.usos} ensaio(s) já se
          ampararam nela.
          <div style={{ marginTop: '.4rem' }}>{vigente.motivo}</div>
          {pode('equipamento.liberar_bloqueado') && (
            <button
              type="button" className="botao discreto"
              style={{ marginTop: '.6rem' }}
              onClick={() => setRevogando(vigente)}
            >
              Revogar
            </button>
          )}
        </Aviso>
      )}

      {impedido && !vigente && !pode('equipamento.liberar_bloqueado') && (
        <Aviso tipo="info" titulo="Você não pode autorizar">
          Autorizar o uso de equipamento impedido é do Responsável Técnico ou
          do administrador. Procure um deles — o sistema vai pedir a
          justificativa e o prazo.
        </Aviso>
      )}

      {lista.length > 0 && (
        <div className="rola">
          <table className="dados">
            <thead>
              <tr>
                <th>Autorizada em</th><th>Motivo e impedimento</th>
                <th>Vale até</th><th>Usos</th><th>Situação</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((a) => (
                <tr key={a.id} style={a.revogada ? { opacity: .55 } : undefined}>
                  <td>
                    {dataHora(a.autorizado_em)}
                    <div className="sub">{a.autorizado_por_nome ?? '—'}</div>
                  </td>
                  <td>
                    {a.motivo}
                    {a.impedimento_no_ato && (
                      <div className="sub">impedimento: {a.impedimento_no_ato}</div>
                    )}
                    {a.revogada && (
                      <div className="sub">
                        revogada por {a.revogada_por_nome ?? '—'}:{' '}
                        {a.motivo_revogacao}
                      </div>
                    )}
                  </td>
                  <td>{dataHora(a.valida_ate)}</td>
                  <td className="num">{a.usos}</td>
                  <td>
                    {a.revogada
                      ? <Selo tom="neutro">Revogada</Selo>
                      : a.vigente
                        ? <Selo tom="atencao">Vigente</Selo>
                        : <Selo tom="neutro">Vencida</Selo>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {autorizando && (
        <FormAutorizar
          equipamento={equipamento}
          aoFechar={(mudou) => {
            setAutorizando(false);
            if (mudou) { carregar(); aoMudar(); }
          }}
        />
      )}
      {revogando && (
        <FormRevogar
          autorizacao={revogando}
          aoFechar={(mudou) => {
            setRevogando(null);
            if (mudou) { carregar(); aoMudar(); }
          }}
        />
      )}
    </div>
  );
}

function FormAutorizar({
  equipamento, aoFechar,
}: { equipamento: string; aoFechar(mudou: boolean): void }) {
  const [motivo, setMotivo] = useState('');
  // Sete dias: prazo curto o bastante para obrigar a revisão, longo o
  // bastante para o certificado voltar do laboratório de calibração.
  const [dias, setDias] = useState('7');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const enviar = async () => {
    setEnviando(true); setErro(null);
    try {
      await autorizarUso(equipamento, { motivo, dias: Number(dias) });
      aoFechar(true);
    } catch (e) { setErro(e); setEnviando(false); }
  };

  return (
    <Dialogo aberto titulo="Autorizar uso excepcional" aoFechar={() => aoFechar(false)}>
      <Aviso tipo="atencao" titulo="Isto vai para o laudo">
        Todo ensaio feito sob esta autorização carrega o seu nome e esta
        justificativa. É o que o cliente e o auditor vão ler se o resultado
        for questionado.
      </Aviso>

      <ErroDaApi erro={erro} />

      <Area
        rotulo="Justificativa"
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        rows={3}
        dica="O motivo real e o que já foi providenciado. Ex.: «certificado em
              renovação na Suporte, entrega prevista para 27/08; obra parada
              aguardando o ensaio»."
      />

      <Campo
        rotulo="Prazo, em dias"
        type="number"
        min={1}
        max={90}
        value={dias}
        onChange={(e) => setDias(e.target.value)}
        dica="Máximo de 90 dias. Exceção sem fim deixa de ser exceção."
      />

      <div className="linha" style={{ marginTop: '.9rem' }}>
        <button
          type="button" className="botao" onClick={enviar}
          disabled={enviando || motivo.trim().length < 15}
        >
          {enviando ? 'Autorizando…' : 'Autorizar'}
        </button>
        <button
          type="button" className="botao secundario"
          onClick={() => aoFechar(false)}
        >
          Cancelar
        </button>
      </div>
    </Dialogo>
  );
}

function FormRevogar({
  autorizacao, aoFechar,
}: { autorizacao: AutorizacaoUso; aoFechar(mudou: boolean): void }) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const enviar = async () => {
    setEnviando(true); setErro(null);
    try { await revogarAutorizacao(autorizacao.id, motivo); aoFechar(true); }
    catch (e) { setErro(e); setEnviando(false); }
  };

  return (
    <Dialogo aberto titulo="Revogar autorização" aoFechar={() => aoFechar(false)}>
      <Aviso tipo="info" titulo="Os ensaios já feitos continuam válidos">
        Revogar impede novos usos daqui para a frente. Os {autorizacao.usos}{' '}
        ensaio(s) que já se ampararam nela continuam com o registro da
        autorização — o passado não se reescreve.
      </Aviso>

      <ErroDaApi erro={erro} />

      <Area
        rotulo="Por quê"
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        rows={2}
        dica="Ex.: «certificado novo chegou e foi registrado»."
      />

      <div className="linha" style={{ marginTop: '.9rem' }}>
        <button type="button" className="botao" onClick={enviar} disabled={enviando}>
          {enviando ? 'Revogando…' : 'Revogar'}
        </button>
        <button
          type="button" className="botao secundario"
          onClick={() => aoFechar(false)}
        >
          Voltar
        </button>
      </div>
    </Dialogo>
  );
}
